// The new site (src/pages/home: the combined home page "/", /events for events and photographers, /personal for
// the Photohawk app, and the app's web pages /personal/*) is STAGING ONLY until the owner approves it for
// production. Where it's off, "/" is today's home page and the new URLs (and the old staging URL /business) are 404s.
//
// The switch is the deployment, not the visitor: the SSR server's pinned PHOTOHAWK_ENV (which the server also
// injects into the page as window.__PH_ENV__). A tester's ?phEnv=… / localStorage override (src/lib/env.ts)
// deliberately does NOT turn it on, so photohawk.com?phEnv=staging still shows today's home page. When neither
// is known (unit tests, a bare `vite` dev server) the host decides: photohawk.com and *.photohawk.com are prod.
//
// Launch: set NEW_HOME_IN_PROD to true in a reviewed PR. The Photohawk Family app's own pages (/personal/privacy,
// /personal/terms, /personal/delete-account, /personal/support) don't wait for it: they are public in production from
// 20 October 2026 (src/App.tsx), in today's site frame there.

export const NEW_HOME_IN_PROD = false;

export type DeploymentEnv = "staging" | "prod";

const hostIsProd = (host: string): boolean => {
  const h = (host || "").toLowerCase().split(":")[0];
  return h === "photohawk.com" || h.endsWith(".photohawk.com");
};

/** The env this deployment serves, from the server's PHOTOHAWK_ENV (SSR) or its injected copy (browser). */
export const deploymentEnv = (host?: string): DeploymentEnv => {
  let pinned: string | undefined;
  if (typeof window === "undefined") {
    try {
      pinned = typeof process !== "undefined" ? process.env?.PHOTOHAWK_ENV : undefined;
    } catch {
      pinned = undefined;
    }
  } else {
    pinned = (window as unknown as { __PH_ENV__?: string }).__PH_ENV__;
  }
  if (pinned === "prod" || pinned === "staging") return pinned;
  const h = host ?? (typeof window !== "undefined" ? window.location.hostname : "");
  return hostIsProd(h) ? "prod" : "staging";
};

/** Whether the new home page and the Personal web pages are on for a deployment env. */
export const newHomeEnabledFor = (env: DeploymentEnv, inProd: boolean = NEW_HOME_IN_PROD): boolean =>
  env !== "prod" || inProd;

/** Whether this deployment shows the new home page (staging: yes; prod: no until launch). */
export const isNewHomeEnabled = (host?: string): boolean => newHomeEnabledFor(deploymentEnv(host));
